/**
 * @docs docs/features/website-navigation-shell
 */

import React from 'react';
import useContext from '@/client/context';
import WordmarkInk from '@/client/assets/identity/wordmark-ink.png';
import {
    LANDING_DOMAINS_FOOTER_ITEMS,
    LANDING_PRODUCT_FOOTER_ITEMS,
    LANDING_SOLUTION_FOOTER_ITEMS,
} from '@/client/catalogs/landing/pageContent';
import { track } from '@/client/pages/landing/lib/analytics';
import Icon, { type SpriteIconName } from '@/client/components/ui/icon/Icon';
import { trackConversionTrigger } from '@/client/pages/landing/lib/trackConversionTrigger';
import { trackLandingCta } from '@/client/pages/landing/lib/trackLandingCta';
import { FOOTER_COMPARE_PAGE_REGISTRY, comparePagePath } from '@/common/catalogs/landing/comparePages';
import { LANDING_WEBSITE_PAGE_ROUTES } from '@/common/catalogs/landing/publicPages';
import { isPublishedHumanPageTarget } from '@/common/catalogs/landing/internalLinks';
import {
    DOMAINING_NEWS_FEED_PATH,
    DOMAINING_NEWS_INDEX_PATH,
} from '@uniquedomains/components/contracts/news/public';
import type { Links } from '../../types';
import FooterColumn from './FooterColumn';
import { HEADER_LOGIN_TRIGGER_BY_PAGE, resolveHeaderTriggerId } from './Header';

const SOCIAL_ICON_LINKS: readonly { label: string; href: string; icon: SpriteIconName }[] = [
    { label: 'LinkedIn', href: 'https://www.linkedin.com/company/uniquedomains', icon: 'linkedin' },
    { label: 'Instagram', href: 'https://www.instagram.com/kai.etan/', icon: 'instagram' },
    { label: 'X', href: 'https://x.com/UniqueDomainsHQ', icon: 'x' },
];
function resolveFooterHref(href: string, sectionHrefPrefix: string): string {
    if (!href.startsWith('#') || !sectionHrefPrefix) {
        return href;
    }

    return `${sectionHrefPrefix}${href}`;
}

export type FooterProps = {
    links: Links;
    sectionHrefPrefix?: string;
    trackingPage?: string;
};

export function Footer({ links, sectionHrefPrefix = '', trackingPage = 'landing' }: FooterProps) {
    const { enforceDomainPublicationLinks, renderedDomainResourcePaths } = useContext();
    const publishedDomainPaths = enforceDomainPublicationLinks
        ? renderedDomainResourcePaths
        : undefined;
    const publishedDomainFooterItems = LANDING_DOMAINS_FOOTER_ITEMS.filter((item) =>
        isPublishedHumanPageTarget(item.href, publishedDomainPaths),
    );
    const currentYear = new Date().getFullYear();
    // Phone accordion: one group open at a time. Desktop ignores it, every column is shown.
    const [openColumn, setOpenColumn] = React.useState<string | null>(null);
    const columnState = (title: string) => ({
        isOpen: openColumn === title,
        onToggle: () => setOpenColumn((current) => (current === title ? null : title)),
    });
    const socialLinkClass =
        'inline-flex h-[42px] w-[42px] items-center justify-center rounded-full border border-[#dfdcd4] bg-white text-[#4a4d4e] transition hover:border-slate-300 hover:text-slate-950 lg:h-10 lg:w-10 lg:shadow-[0_8px_20px_rgba(27,29,30,0.05)]';

    return (
        <footer class="mt-11 border-t border-[#edeae2] lg:mt-0 lg:border-[#ffffff99]">
            <div class="mx-auto max-w-[1240px] px-5 pb-0 pt-8 sm:px-6 lg:grid lg:grid-cols-3 lg:gap-8 lg:px-8 lg:pb-12 lg:pt-10 xl:grid-cols-[1.2fr_0.72fr_0.72fr_0.72fr_0.8fr_0.75fr_0.6fr]">
                <div class="mb-[26px] border-b border-[#e6e2da] pb-[26px] lg:mb-0 lg:border-0 lg:pb-0">
                    <img src={WordmarkInk} alt="Unique Domains" width="159" height="24" class="h-[22px] w-auto lg:h-6" />
                    <p class="mt-3.5 max-w-[24rem] text-sm leading-relaxed text-[#66696b] lg:mt-4 lg:text-slate-600">
                        Where domain decisions are made.
                    </p>
                    <p class="mt-4 hidden text-xs text-slate-600 lg:block">© {currentYear} Unique Domains</p>

                    <div class="mt-[18px] flex items-center gap-2.5 lg:mt-6">
                        {SOCIAL_ICON_LINKS.map(({ label, href, icon }) => (
                            <a
                                key={label}
                                href={href}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label={label}
                                class={socialLinkClass}
                            >
                                <Icon name={icon} size={16} strokeWidth={1.9} />
                            </a>
                        ))}
                        <a
                            href={links.github}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="GitHub"
                            class={socialLinkClass}
                        >
                            <Icon name="github" size={16} strokeWidth={1.9} />
                        </a>
                    </div>
                </div>

                <FooterColumn title="Domains" {...columnState('Domains')}>
                        {publishedDomainFooterItems.map((item) => (
                            <a key={item.href} href={item.href} class="block transition hover:text-slate-950">
                                {item.label}
                            </a>
                        ))}
                </FooterColumn>

                <FooterColumn title="Product" {...columnState('Product')}>
                        {LANDING_PRODUCT_FOOTER_ITEMS.map((item) => (
                            <a
                                key={item.id}
                                href={resolveFooterHref(links[item.linkId], sectionHrefPrefix)}
                                class="block transition hover:text-slate-950"
                            >
                                {item.label}
                            </a>
                        ))}
                </FooterColumn>

                <FooterColumn title="Solutions" {...columnState('Solutions')}>
                        {LANDING_SOLUTION_FOOTER_ITEMS.map((item) => (
                            <a
                                key={item.id}
                                href={links[item.linkId]}
                                class="block transition hover:text-slate-950"
                            >
                                {item.footerLabel}
                            </a>
                        ))}
                        <a
                            href={LANDING_WEBSITE_PAGE_ROUTES.team}
                            class="block transition hover:text-slate-950"
                        >
                            About
                        </a>
                        <a href={links.pricing} class="block transition hover:text-slate-950">
                            Pricing
                        </a>
                </FooterColumn>

                <FooterColumn title="Access" {...columnState('Access')}>
                        {/*
                          * The site's one login control outside the phone menu, since Login
                          * left the desktop bar on 2026-09-17. It fires the page's own
                          * `*_header_login` id rather than a new footer id, so the login
                          * intent stays one row per page in the funnel wherever it is clicked,
                          * told apart by `section`. The footer-scoped `*_footer_sign_in` ids
                          * stay in the shared catalog, which Product pins, and stop firing here.
                          */}
                        <a
                            href={links.signIn}
                            onClick={() => {
                                trackLandingCta({
                                    page: trackingPage,
                                    section: 'footer_access',
                                    label: 'Log in',
                                    kind: 'auth',
                                    href: links.signIn,
                                    surface: 'footer',
                                });
                                trackConversionTrigger(
                                    resolveHeaderTriggerId(
                                        trackingPage,
                                        HEADER_LOGIN_TRIGGER_BY_PAGE,
                                        'website_landing_header_login',
                                    ),
                                    {
                                        href: links.signIn,
                                        page: trackingPage,
                                        section: 'footer_access',
                                    },
                                );
                            }}
                            data-testid="footer-login-link"
                            class="block transition hover:text-slate-950"
                        >
                            Log in
                        </a>
                        <a href={`${links.pricing}#plans`} class="block transition hover:text-slate-950">
                            Pricing
                        </a>
                        <a href={links.apiDocs} class="block transition hover:text-slate-950">
                            API Docs
                        </a>
                        <a href={DOMAINING_NEWS_INDEX_PATH} class="block transition hover:text-slate-950">
                            Domaining News
                        </a>
                        {/* The Market menu's dated page, beside the news it shares that menu with. */}
                        <a href={LANDING_WEBSITE_PAGE_ROUTES.agenda} class="block transition hover:text-slate-950">
                            Agenda
                        </a>
                        <a href={DOMAINING_NEWS_FEED_PATH} class="block transition hover:text-slate-950">
                            News RSS
                        </a>
                        <a href={links.newsletter} class="block transition hover:text-slate-950">
                            Newsletter
                        </a>
                        <a href={links.glossary} class="block transition hover:text-slate-950">
                            Glossary
                        </a>
                        <a
                            href="/llms.txt"
                            class="block transition hover:text-slate-950"
                            onClick={() =>
                                track('ai_source_clicked', {
                                    surface: 'footer',
                                    target: 'llms',
                                    page: trackingPage,
                                })
                            }
                        >
                            AI source
                        </a>
                        <a
                            href="/llms-security.txt"
                            class="block transition hover:text-slate-950"
                            onClick={() =>
                                track('ai_source_clicked', {
                                    surface: 'footer',
                                    target: 'security',
                                    page: trackingPage,
                                })
                            }
                        >
                            AI security
                        </a>
                </FooterColumn>

                <FooterColumn title="Compare" testId="footer-compare" {...columnState('Compare')}>
                        <a
                            href={LANDING_WEBSITE_PAGE_ROUTES.compare}
                            class="block transition hover:text-slate-950"
                        >
                            All comparisons
                        </a>
                        {FOOTER_COMPARE_PAGE_REGISTRY.map((entry) => (
                            <a
                                key={entry.slug}
                                href={comparePagePath(entry.slug)}
                                class="block transition hover:text-slate-950"
                            >
                                {entry.footerLabel || entry.competitorShortName}
                            </a>
                        ))}
                </FooterColumn>

                <FooterColumn title="Legal" testId="footer-legal" {...columnState('Legal')}>
                        <a
                            href={LANDING_WEBSITE_PAGE_ROUTES.terms}
                            class="block transition hover:text-slate-950"
                        >
                            Terms
                        </a>
                        <a
                            href={LANDING_WEBSITE_PAGE_ROUTES.privacy}
                            class="block transition hover:text-slate-950"
                        >
                            Privacy
                        </a>
                        <a
                            href={LANDING_WEBSITE_PAGE_ROUTES.refundPolicy}
                            class="block transition hover:text-slate-950"
                        >
                            Refunds
                        </a>
                </FooterColumn>
            </div>
            <div class="mx-auto max-w-[1240px] pb-7 lg:px-8 lg:pb-14">
                <p class="mt-[18px] px-5 text-xs text-[#66696b] sm:px-6 lg:hidden">© {currentYear} Unique Domains</p>
                {/* Intrinsic size reserves the box, so the lazy wordmark cannot shift the page. */}
                <img
                    src={WordmarkInk}
                    alt=""
                    aria-hidden="true"
                    width="1698"
                    height="256"
                    loading="lazy"
                    decoding="async"
                    class="mt-[26px] block h-auto w-full select-none lg:mt-8"
                />
            </div>
        </footer>
    );
}
